import Typography from "@mui/joy/Typography";
import { memo, useState } from "react";
import { useRecoilValue } from "recoil";
import useOpenTask from "hooks/useOpenTask";
import { userAtom } from "recoil/userAtom";
import { IncompleteChip } from "./TaskChip";
import { Box, Stack } from "@mui/joy";
import CustomModal from "components/common/CustomModal";
import useGetTaskTitleFromTaskKey from "hooks/useGetTaskTitleFromTaskKey";
import useOpenThoughtRecord from "hooks/useOpenThoughtRecord";
import { getTaskIdFromTaskKey } from "logic/logics";
import useOpenNewThoughtRecord from "hooks/useOpenNewThoughtRecord";
import { extractHexColor } from "styles/hexOpacity";
import { theme } from "styles/theme";
import useOpenMeditationRecord from "../../hooks/useOpenMeditationRecord";
import useOpenNewMeditationRecord from "../../hooks/useOpenNewMeditationRecord";

const CheckIcon = () => {
  return (
    <svg
      width="11"
      height="8"
      viewBox="0 0 11 8"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
    >
      <path
        d="M1 3.5L4 6.5L9.5 1"
        stroke="white"
        strokeWidth="2"
        strokeLinecap="round"
        stroke-linejoin="round"
      />
    </svg>
  );
};

export function Status({
  isDone,
  isOpen,
  isOptional,
}: {
  isDone: boolean;
  isOpen: boolean;
  isOptional?: boolean;
}) {
  return (
    <Stack
      sx={{
        width: "20px",
        height: "20px",
        borderRadius: "50%",
        ...(!isDone
          ? {
              borderWidth: "2px",
              borderColor:
                isOpen && !isOptional
                  ? "primary.solid"
                  : theme.vars.palette.Content.Weak,
            }
          : {
              backgroundColor: theme.vars.palette.Content.Weak,
            }),
        justifyContent: "center",
        alignItems: "center",
      }}
    >
      {isDone && <CheckIcon />}
    </Stack>
  );
}

export function LockIcon() {
  const color = extractHexColor(theme.vars.palette.primary.solid);
  return (
    <svg
      width="20"
      height="20"
      viewBox="0 0 20 20"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
    >
      <path
        d="M14 8V6C14 3.79086 12.2091 2 10 2V2C7.79086 2 6 3.79086 6 6V8"
        stroke={color}
        stroke-width="2"
      />
      <path
        fill-rule="evenodd"
        clip-rule="evenodd"
        d="M4 8C2.89543 8 2 8.89543 2 10V16C2 17.1046 2.89543 18 4 18H16C17.1046 18 18 17.1046 18 16V10C18 8.89543 17.1046 8 16 8H4ZM12 12C12 12.9319 11.3626 13.715 10.5 13.937V15.5C10.5 15.7761 10.2761 16 10 16C9.72386 16 9.5 15.7761 9.5 15.5V13.937C8.63739 13.715 8 12.9319 8 12C8 10.8954 8.89543 10 10 10C11.1046 10 12 10.8954 12 12Z"
        fill={color}
      />
    </svg>
  );
}

function TaskItemTemplate({
  taskKey,
  isOpen,
  isDone,
  isLocked,
  isOptional,
  hasMissingValue,
  disableDirectAction,
  linkedThoughtRecordKey,
  linkedMeditationRecordKey,
  setNumber,
  hasConnectionLine,
  connectionTop,
  connectionHeight,
  isFirstOfSet,
  isLastOfSet,
}: {
  taskKey: string;
  isOpen: boolean;
  isDone: boolean;
  isLocked?: boolean;
  isOptional?: boolean;
  hasMissingValue?: boolean;
  disableDirectAction?: boolean;
  linkedThoughtRecordKey?: string;
  linkedMeditationRecordKey?: string;
  setNumber?: number;
  hasConnectionLine?: boolean;
  connectionTop?: number;
  connectionHeight?: number;
  isFirstOfSet?: boolean;
  isLastOfSet?: boolean;
}) {
  const openTask = useOpenTask({ taskKey: taskKey });
  const openThoughtRecord = useOpenThoughtRecord(linkedThoughtRecordKey);
  const openMeditationRecord = useOpenMeditationRecord(linkedMeditationRecordKey);
  const openNewThoughtRecord = useOpenNewThoughtRecord();
  const openNewMeditationRecord = useOpenNewMeditationRecord();
  const openPayment = useOpenTask({ taskKey: "payment" });
  const user = useRecoilValue(userAtom);
  const isCoach = user?.role !== "patient";

  const [isConfirmOpen, setIsConfirmOpen] = useState(false);
  const [confirmModalType, setConfirmModalType] = useState<string>("생각기록지");
  const [confirmAction, setConfirmAction] = useState<(() => void) | null>(null);

  const title = useGetTaskTitleFromTaskKey(taskKey);
  return (
    <Stack
      direction="row"
      alignItems={"center"}
      sx={{
        position: "relative",
        cursor: "pointer",
        px: "12px",
        py: "8px",
        borderRadius: "12px",
        "@media (min-width: 1024px)": {
          "&:hover": {
            backgroundColor: theme.vars.palette.Surface.Backward,
          },
        },
        "@media (max-width: 1024px)": {
          "&:hover": {
            backgroundColor: theme.vars.palette.Surface.Forward,
          },
        },
        "&:active": {
          backgroundImage: `linear-gradient(${theme.vars.palette.hoverLight}, ${theme.vars.palette.hoverLight})`,
        },
        "&:focus": {
          backgroundColor: theme.vars.palette.Surface.Forward,
        },
      }}
      onClick={() => {
        (document.activeElement as HTMLElement)?.blur();
        if (!disableDirectAction) {
          if (isLocked) {
            openPayment();
          } else {
            if (
              getTaskIdFromTaskKey(taskKey) === "0-7-A" ||
              getTaskIdFromTaskKey(taskKey) === "7-2-A" ||
              getTaskIdFromTaskKey(taskKey) === "9-5-A" ||
              getTaskIdFromTaskKey(taskKey) === "9-7-A" ||
              getTaskIdFromTaskKey(taskKey) === "9-9-A" ||
              getTaskIdFromTaskKey(taskKey) === "9-11-A"
            ) {
              const proceed = linkedThoughtRecordKey
                ? openThoughtRecord
                : openNewThoughtRecord;

              if (!isDone && !isCoach) {
                setConfirmModalType("생각기록지")
                setConfirmAction(() => proceed);
                setIsConfirmOpen(true);
              } else {
                proceed();
              }
            } else if (getTaskIdFromTaskKey(taskKey).includes("90-1-A")) {
              const proceed = linkedMeditationRecordKey
                ? openMeditationRecord
                : openNewMeditationRecord;

              if (!isDone && !isCoach) {
                setConfirmModalType("명상훈련지")
                setConfirmAction(() => proceed);
                setIsConfirmOpen(true);
              } else {
                proceed();
              }
            } else {
              openTask();
            }
          }
        }
      }}
    >
      <Stack
        direction="row"
        alignItems={"center"}
        spacing={"8px"}
        sx={{ flex: 1 }}
      >
        {!isLocked ? (
          <Status isOpen={isOpen} isDone={isDone} isOptional={isOptional} />
        ) : (
          <LockIcon />
        )}
        {hasConnectionLine && (
          <Box
            sx={{
              width: "8px",
              height: `${connectionHeight}px`,
              position: "absolute",
              top: `${connectionTop}px`,
              left: "-16px",
              borderRadius: `${isFirstOfSet ? 4 : 0}px 0px 0px ${
                isLastOfSet ? 4 : 0
              }px`,
              borderWidth: `1px 0px ${isLastOfSet ? 1 : 0}px 1px`,
              borderColor: theme.vars.palette.Content.SuperWeak,
            }}
          ></Box>
        )}

        <Typography
          variant={"Body"}
          sx={{
            flex: 1,
            fontWeight: 500,
            color: theme.vars.palette.Content.Strong,
            px: 0,
          }}
        >
          {title}
        </Typography>
      </Stack>

      {isCoach && hasMissingValue && <IncompleteChip />}

      <CustomModal
        open={isConfirmOpen}
        onClose={() => setIsConfirmOpen(false)}
        title={`<${confirmModalType}>로 이동할게요.`}
        subtitle={"그곳에서 활동지를 계속 진행해주세요."}
        actions={[
          {
            label: "확인",
            onClick: () => {
              const action = confirmAction;
              setIsConfirmOpen(false);
              setConfirmAction(null);
              if (action) action();
            },
            variant: "Solid",
          },
        ]}
      />
    </Stack>
  );
}

export default memo(TaskItemTemplate);
